import { useEffect, useState } from 'react';
import type { EmailCheck } from '../sdk/email-check';
import { EMAIL_RE } from '../sdk/identity-ask';

const CHECK_DEBOUNCE_MS = 350;

/**
 * True once floe-api has refused the email currently typed as an obvious fake.
 *
 * Checked while the visitor types, never on the tap: the Continue/Start tap is
 * the gesture the mic prompt rides on (FLO-109), so it must not wait on the
 * network. Until an answer arrives the address counts as fine, and floe-api
 * re-checks whatever is finally stored.
 *
 * `trusted` skips the address the host page or a demo link supplied.
 */
export const useEmailRefused = (
  email: string,
  check: EmailCheck | undefined,
  trusted?: string,
): boolean => {
  const value = email.trim().toLowerCase();
  const skip = trusted?.trim().toLowerCase();
  const [refused, setRefused] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    if (!check || value === skip || !EMAIL_RE.test(value)) {
      return;
    }
    let live = true;
    const ask = async () => {
      const verdict = await check(value);
      if (live && !verdict.plausible) {
        setRefused((previous) => new Set(previous).add(value));
      }
    };
    const timer = setTimeout(() => {
      void ask();
    }, CHECK_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [check, value, skip]);

  return refused.has(value);
};
